import { Amplify, Auth } from "aws-amplify";
import React, { useEffect, useState } from "react";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import "react-select-search/style.css";
import "./App.css";
import { COGNITO } from "./generated-aws";
import { UserInfo } from "./models/user-info";
import AcademicDashboard from "./screens/AcademicDashboard";
import AcademicEmailComposer from "./screens/AcademicEmailComposer.screen";
import AcademicEmailStatus from "./screens/AcademicEmailStatus.screen";
import AccessScreen from "./screens/Access.screen";
import ApprovalDetailScreen from "./screens/ApprovalDetail.screen";
import ApprovalListScreen from "./screens/ApprovalList.screen";
import ConfirmationScreen from "./screens/Confirmation.screen";
import CreateScreen from "./screens/Create.screen";
import EditDetailScreen from "./screens/EditDetail.screen";
import EditListScreen from "./screens/EditList.screen";
import ExportScreen from "./screens/Export.screen";
import ImportEmailRecipients from "./screens/ImportEmailRecipients.screen";
import LoginScreen from "./screens/Login.screen";
import ProtectedRoute from "./screens/ProtectedRoute";
import PushNotification from "./screens/PushNotification.screen";
import SignUpScreen from "./screens/SignUp.screen";

Amplify.configure({
  aws_cognito_region: COGNITO.REGION,
  aws_user_pools_id: COGNITO.USER_POOL_ID,
  aws_user_pools_web_client_id: COGNITO.APP_CLIENT_ID,
  Auth: {
    oauth: {
      domain: `${COGNITO.USER_POOL_DOMAIN}.auth.ap-southeast-2.amazoncognito.com`,
      scope: ["email", "profile", "openid"],
      redirectSignIn: `${window.location.origin}`,
      redirectSignOut: `${window.location.origin}`,
      responseType: "code",
    },
  },
});

const App: React.FC = () => {
  const [userInfo, setUserInfo] = useState<UserInfo>({
    AuthorUnikey: "",
    AuthorName: "",
    Email: "",
    isCoordinator: false,
    isEditor: false,
  });

  useEffect(() => {
    Auth.currentSession().then((user) => {
      const attributes = user.getIdToken().payload;
      if (process.env.REACT_APP_OKTA_DISABLED === "true") {
        setUserInfo({
          AuthorUnikey: attributes["cognito:username"],
          AuthorName: attributes["cognito:username"],
          Email: attributes.email,
          isCoordinator: attributes["custom:coordinator"],
          isEditor: attributes["custom:editor"],
        });
      } else {
        setUserInfo({
          AuthorUnikey: attributes["custom:unikey"],
          AuthorName: `${attributes["custom:givenName"]} ${attributes["custom:surname"]}`,
          Email: attributes.email,
          isCoordinator: attributes["custom:coordinator"],
          isEditor: attributes["custom:editor"],
        });
      }
    });
  }, []);

  return (
    <>
      <Router>
        <Routes>
          <Route
            path="/"
            element={
              <ProtectedRoute
                component={() => <CreateScreen userInfo={userInfo} />}
              />
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute
                accessRequired={["approver", "editor", "coordinator"]}
                component={() => <AcademicDashboard userInfo={userInfo} />}
              />
            }
          />
          <Route path="/signup" element={<SignUpScreen />} />
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/confirmation" element={<ConfirmationScreen />} />
          <Route
            path="/edit"
            element={
              <ProtectedRoute
                component={() => <EditListScreen userInfo={userInfo} />}
                accessRequired={["editor", "approver"]}
              />
            }
          />
          <Route
            path="/edit/:uuid"
            element={
              <ProtectedRoute
                component={() => <EditDetailScreen userInfo={userInfo} />}
                accessRequired={["editor", "approver"]}
              />
            }
          />
          <Route
            path="/approvals"
            element={
              <ProtectedRoute
                component={() => <ApprovalListScreen userInfo={userInfo} />}
                accessRequired={["approver"]}
              />
            }
          />
          <Route
            path="/approvals/:uuid"
            element={
              <ProtectedRoute
                component={() => <ApprovalDetailScreen userInfo={userInfo} />}
                accessRequired={["approver"]}
              />
            }
          />
          <Route
            path="/access"
            element={
              <ProtectedRoute
                component={() => <AccessScreen userInfo={userInfo} />}
                accessRequired={["superadmin"]}
              />
            }
          />
          <Route
            path="/academic-compose-email"
            element={
              <ProtectedRoute
                accessRequired={["approver", "editor", "coordinator"]}
                component={() => <AcademicEmailComposer userInfo={userInfo} />}
              />
            }
          />
          <Route
            path="/academic-email-status"
            element={
              <ProtectedRoute
                accessRequired={["approver", "editor", "coordinator"]}
                component={() => <AcademicEmailStatus userInfo={userInfo} />}
              />
            }
          />
          <Route
            path="/send-notification"
            element={
              <ProtectedRoute
                accessRequired={["approver", "editor", "coordinator"]}
                component={() => <PushNotification userInfo={userInfo} />}
              />
            }
          />
          <Route
            path="/import-email-recipients"
            element={
              <ProtectedRoute
                accessRequired={["superadmin"]}
                component={() => <ImportEmailRecipients userInfo={userInfo} />}
              />
            }
          />
          <Route
            path="/export"
            element={
              <ProtectedRoute
                accessRequired={["approver"]}
                component={() => <ExportScreen userInfo={userInfo} />}
              />
            }
          />
        </Routes>
      </Router>
    </>
  );
};

export default App;
